import { useCallback, useEffect, useState } from 'react'
import { modelsForAgent, setOpencodeModels, type AgentId, type ModelSpec } from '@/data/llmCatalog'
import { getOpencodeModels } from '@/api/settings'

/**
 * The model list for an agent CLI. Static for Claude Code / Codex; for OpenCode
 * it is read LIVE from the server's CLI — whatever the providers configured in
 * OpenCode expose — and cached into `modelsForAgent('opencode')` so every
 * picker reading the catalog sees the same list.
 */
export function useAgentModels(agent: AgentId | null | undefined) {
  const [models, setModels] = useState<ModelSpec[]>(() => (agent ? modelsForAgent(agent) : []))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (refresh = false) => {
    if (agent !== 'opencode') {
      setModels(agent ? modelsForAgent(agent) : [])
      setError('')
      return
    }
    setLoading(true)
    try {
      const res = await getOpencodeModels(refresh)
      setModels(setOpencodeModels(res.models || []))
      setError(
        !res.status?.installed
          ? 'OpenCode CLI is not installed on the server (npm i -g opencode-ai).'
          : (res.models || []).length === 0
            ? (res.status?.error || 'OpenCode lists no models — configure a provider with `opencode auth login` on the server.')
            : '',
      )
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load OpenCode models')
    } finally {
      setLoading(false)
    }
  }, [agent])

  useEffect(() => {
    load(false)
  }, [load])

  return { models, loading, error, refresh: () => load(true) }
}
